import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import CountUp from 'react-countup';
import { useLanguage } from '../../contexts/LanguageContext';

function StatsCard({ 
  title, 
  value, 
  change, 
  changeType = 'positive', 
  icon: Icon, 
  gradient,
  description,
  trend,
  periodText,
  trendPeriodText,
  animated = true,
  size = 'default', // 'small', 'default', 'large'
  showProgressBar = false,
}) {
  const { t } = useLanguage();
  const sc = t.statsCard;
  const c = t.common;
  const resolvedPeriodText = periodText ?? sc.vsLastPeriod;
  const resolvedTrendPeriodText = trendPeriodText ?? sc.last7Days;

  const changeColor = changeType === 'positive'
    ? 'text-success-600 '
    : changeType === 'negative'
      ? 'text-error-600 '
      : 'text-secondary-400 ';

  const changeIcon = changeType === 'positive' ? '↗' : 
                    changeType === 'negative' ? '↘' : 
                    '→';

  const sizeClasses = {
    small: 'p-4',
    default: 'p-4 xl:p-5',
    large: 'p-5 sm:p-7'
  };

  const titleSizes = {
    small: 'text-sm',
    default: 'text-base',
    large: 'text-lg'
  };

  const valueSizes = {
    small: 'text-xl',
    default: 'text-2xl sm:text-3xl',
    large: 'text-3xl sm:text-4xl'
  };

  const iconSizes = {
    small: 'w-8 h-8',
    default: 'w-12 h-12',
    large: 'w-16 h-16'
  };

  const trendValues = Array.isArray(trend)
    ? trend.map((point) => Number(point) || 0)
    : [];
  const trendMax = trendValues.length > 0 ? Math.max(1, ...trendValues) : 1;
  const TREND_BAR_HEIGHT_PX = 28;

  const headerMinHeights = {
    small: 'min-h-[3.5rem]',
    default: 'min-h-[4rem]',
    large: 'min-h-[4.5rem]',
  };

  const valueMinHeights = {
    small: 'min-h-[2.25rem]',
    default: 'min-h-[2.75rem] sm:min-h-[3rem]',
    large: 'min-h-[3.25rem] sm:min-h-[3.5rem]',
  };

  const reducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={reducedMotion ? undefined : { y: -4, transition: { type: 'spring', stiffness: 400, damping: 24 } }}
      className={`card-premium group relative flex h-full min-w-0 flex-col overflow-hidden ${sizeClasses[size]}`}
    >
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br from-orange-400/10 to-amber-400/5 blur-3xl  "
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        aria-hidden="true"
        style={{
          background:
            'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)',
        }}
      />

      {/* Header */}
      <div className={`relative mb-3 flex shrink-0 items-start justify-between gap-4 ${headerMinHeights[size]}`}>
        <div className="min-w-0 flex-1">
          <h3 className={`stat-label mb-0.5 line-clamp-2 ${titleSizes[size]}`}>{title}</h3>
          <p
            className={`min-h-[2rem] text-sm leading-snug text-secondary-500 line-clamp-2  ${description ? '' : 'invisible'}`}
            aria-hidden={!description}
          >
            {description || '\u00A0'}
          </p>
        </div>
        
        {Icon && (
          <div className={`flex shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${gradient} ${iconSizes[size]} p-3 shadow-soft ring-1 ring-white/35 transition-transform duration-300 ease-out group-hover:scale-[1.04] `}>
            <Icon className={`${size === 'small' ? 'w-4 h-4' : size === 'large' ? 'w-8 h-8' : 'w-6 h-6'} text-white`} />
          </div>
        )}
      </div>

      {/* Value */}
      <div className={`relative mb-3 shrink-0 ${valueMinHeights[size]}`}>
        <div
          className={`mb-1 font-bold tracking-tight text-secondary-900 tabular-nums  [&_*]:text-inherit ${valueSizes[size]}`}
        >
          {animated && typeof value === 'number' ? (
            <CountUp
              end={value}
              duration={2}
              preserveValue
              separator=","
            />
          ) : (
            value
          )}
        </div>

        <div className={`min-h-[2rem] text-sm ${change !== undefined && change !== null ? changeColor : 'invisible'}`}>
          {(change !== undefined && change !== null) ? (
            <div className="flex flex-wrap items-start gap-x-2 gap-y-0.5">
              <span className="shrink-0 font-semibold text-inherit">
                {change !== 0 ? `${changeIcon} ${Math.abs(change)}%` : '0%'}
              </span>
              <span className="line-clamp-2 text-secondary-500 ">{resolvedPeriodText}</span>
            </div>
          ) : (
            <span aria-hidden="true">{'\u00A0'}</span>
          )}
        </div>
      </div>

      {/* Trend Chart or Additional Info */}
      {trendValues.length > 0 && (
        <div className="mt-auto shrink-0 border-t border-secondary-200/50 pt-3 ">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-secondary-500 ">
            <span className="text-inherit">{resolvedTrendPeriodText}</span>
            <span className={`shrink-0 font-medium ${changeColor}`}>
              {changeType === 'positive' ? c.trendingUp : 
               changeType === 'negative' ? c.trendingDown : 
               c.noActivity}
            </span>
          </div>
          
          {/* Simple trend visualization */}
          <div className="mt-2 h-7 flex items-end gap-1">
            {trendValues.map((point, index) => {
              const barHeight = (point / trendMax) * TREND_BAR_HEIGHT_PX;
              return (
              <motion.div
                key={index}
                initial={{ height: 0 }}
                animate={{ height: barHeight }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`flex-1 rounded-md bg-gradient-to-t ${gradient} opacity-55 group-hover:opacity-90 transition-opacity duration-300 ease-out`}
                style={{ minHeight: point > 0 ? 2 : 0 }}
              />
              );
            })}
          </div>
        </div>
      )}

      {/* Progress bar for explicit percentage values only */}
      {showProgressBar && typeof value === 'number' && value >= 0 && value <= 100 && size !== 'small' && (
        <div className="mt-auto shrink-0 pt-4">
          <div className="w-full bg-secondary-100/80  rounded-full h-1.5 overflow-hidden ring-1 ring-secondary-200/40 ">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${value}%` }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className={`h-full bg-gradient-to-r ${gradient} rounded-full`}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default StatsCard;
